import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    LikeBar,
    ReactionBar,
    Bookmark,
    PresenceBadge,
    Discussion,
} from "../components";


function Section({ id, title, children }) {
    const headingId = title ? `${id}-h` : undefined;

    return (
        <section
            id={id}
            className="vw-sec"
            {...(title
                ? { "aria-labelledby": headingId }
                : { "aria-label": id })}
        >
            {title && (
                <h2 className="vw-h2" id={headingId}>
                    {title}
                </h2>
            )}
            {children}
        </section>
    );
}

const vehicleDetailIcon = (v) => {
    const cls = (v.class || "").toLowerCase();
    const tags = (v.tags || []).map(t => t.toLowerCase());

    if (cls.includes("boat") || tags.includes("boat") || tags.includes("boats"))
        return "🚤";

    if (cls.includes("sub") || tags.includes("submarine"))
        return "🛥️";

    if (cls.includes("helicopter") || tags.includes("helicopters"))
        return "🚁";

    if (cls.includes("plane") || cls.includes("aircraft") || tags.includes("planes"))
        return "✈️";

    if (cls.includes("motorcycle") || tags.includes("motorcycles") || tags.includes("bike"))
        return "🏍️";

    if (cls.includes("truck") || cls.includes("industrial") || tags.includes("utility"))
        return "🚚";

    if (tags.includes("emergency"))
        return "🚓";

    if (tags.includes("military"))
        return "🪖";

    return "🚗";
};

export default function VehicleDetailContent({
    v,
    entityId,
    basePath,
    bookmarkKey,
    yt,
    openLightbox,
    selectedReactionFromBar,
    setSelectedReactionFromBar,
}) {
    if (!v) return null;
    // immediate UI reaction feedback

    return (
        <div className="vw-panel-body">
            {/* Hero */}
            <header className="vw-hero-row">
                <div className="vw-hero-left">
                    <div className="vw-portrait-wrap">
                        {v.portrait ? (
                            <img className="vw-portrait" src={v.portrait} alt={`${v.name} image`} loading="lazy" />
                        ) : (
                            <div
                                className="vw-portrait vw-portrait--placeholder"
                                aria-label="No image"
                            >
                                {vehicleDetailIcon(v)}
                            </div>

                        )}
                    </div>
                </div>

                <div className="vw-hero-right">
                    <div className="vw-chips">
                        {v.make && <span className="vw-chip">🏷 {v.make}</span>}
                        {v.class && <span className="vw-chip">🚘 {v.class}</span>}
                        {typeof v.price === "number" && <span className="vw-chip">💵 ${Number(v.price).toLocaleString("en-US")}</span>}
                        {v.seats != null && <span className="vw-chip">🪑 {v.seats} seats</span>}
                        {v.drivetrain && <span className="vw-chip">🛞 {v.drivetrain}</span>}
                        {v.waterCapable && <span className="vw-chip">🌊 Water-capable</span>}
                        {v.boostNitro && <span className="vw-chip">⚡ Nitro</span>}
                    </div>

                    <h1 className="vw-title">{v.name}</h1>

                    {/* optional: show selected reaction pill */}
                    {selectedReactionFromBar && (
                        <div className="vw-featured-reaction" aria-hidden>
                            {(() => {
                                const id = selectedReactionFromBar;
                                const map = {
                                    love: "❤️ Love",
                                    fire: "🔥 Hot",
                                    funny: "😂 Funny",
                                    cool: "🆒 Cool",
                                    helpful: "💡 Helpful",
                                    applause: "👏 Applause",
                                    curious: "❓ Curious",
                                    bug: "🐞 Issue",
                                };
                                return map[id] || id;
                            })()}
                        </div>
                    )}

                    {/* Description */}
                    {v.description?.trim?.() && (
                        <Section id="desc">
                            <div className="vw-md">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{v.description}</ReactMarkdown>
                            </div>
                        </Section>
                    )}
                </div>
            </header>

            {/* QUICK FACTS */}
            <section className="vw-panel-section">
                <div className="vw-facts-grid">
                    {v.topSpeedKph != null && (
                        <div className="vw-fact">
                            <span>Top speed</span>
                            <strong>{v.topSpeedKph} km/h</strong>
                        </div>
                    )}
                    {v.accel0to100 != null && (
                        <div className="vw-fact">
                            <span>0–100 km/h</span>
                            <strong>{v.accel0to100}s</strong>
                        </div>
                    )}
                    {v.brake100to0m != null && (
                        <div className="vw-fact">
                            <span>Braking</span>
                            <strong>{v.brake100to0m} m</strong>
                        </div>
                    )}
                    {v.handling != null && (
                        <div className="vw-fact">
                            <span>Handling</span>
                            <strong>{v.handling}</strong>
                        </div>
                    )}
                    {v.armor != null && (
                        <div className="vw-fact">
                            <span>Armor</span>
                            <strong>{v.armor}</strong>
                        </div>
                    )}
                </div>
            </section>

            {/* Content */}
            <div className="vw-grid">
                <main className="vw-main">
                    {(v.acquisition?.trim?.() || v.spawnLocations?.length) && (
                        <Section id="acq" title="">
                            <h3 className="vw-h3">How to Get</h3>
                            <div className="vw-md">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {v.acquisition}
                                </ReactMarkdown>
                            </div>
                        </Section>

                    )}

                    {(v.designInspiration?.trim?.() || v.storage?.trim?.() || v.spawnLocations?.length > 0) && (
                        <Section id="notes">
                            {v.designInspiration?.trim?.() && (
                                <>
                                    <h3 className="vw-h3">Design Inspiration</h3>
                                    <div className="vw-md">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {v.designInspiration}
                                        </ReactMarkdown>
                                    </div>
                                </>
                            )}

                            {v.storage?.trim?.() && (
                                <>
                                    <h3 className="vw-h3">Storage</h3>
                                    <div className="vw-md">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {v.storage}
                                        </ReactMarkdown>
                                    </div>
                                </>
                            )}

                            {Array.isArray(v.spawnLocations) && v.spawnLocations.length > 0 && (
                                <>
                                    <h3 className="vw-h3">Spawn Locations</h3>
                                    <div className="vw-md">
                                        <ul className="vw-list">
                                            {v.spawnLocations.map((s, i) => (
                                                <li key={i}>{s}</li>
                                            ))}
                                        </ul>
                                    </div>

                                </>
                            )}
                        </Section>
                    )}


                    {v.performance && Object.values(v.performance).some(Boolean) && (
                        <Section id="performance" title="Performance Ratings">
                            <div className="vw-perf-grid">
                                {Object.entries(v.performance).map(([k, val]) =>
                                    val ? (
                                        <div key={k} className="vw-perf-item">
                                            <span>{k.replace("Stars", "")}</span>
                                            <strong>{"★".repeat(val)}{"☆".repeat(5 - val)}</strong>
                                        </div>
                                    ) : null
                                )}
                            </div>
                        </Section>
                    )}
                    {Array.isArray(v.gallery) && v.gallery.length > 0 && (
                        <Section id="gallery" title="Gallery">
                            <div className="vw-gallery-grid">
                                {v.gallery.map((src, i) => (
                                    <button key={i} className="vw-shotbtn" onClick={() => openLightbox(i)} aria-label={`Open image ${i + 1}`}>
                                        <img className="vw-shot" src={src} alt={`Image ${i + 1}`} loading="lazy" />
                                    </button>
                                ))}
                            </div>
                        </Section>
                    )}

                    {v.videoUrl && (
                        <Section id="video" title="Video">
                            {yt ? (
                                <div className="vw-video">
                                    <iframe
                                        title="Vehicle video"
                                        src={yt}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                        loading="lazy"
                                    />
                                </div>
                            ) : (
                                <a className="vw-link" href={v.videoUrl} target="_blank" rel="noreferrer">▶ Open video</a>
                            )}
                        </Section>
                    )}
                    {v.classOriginal && (
                        <div className="vw-muted">
                            Originally classified as: {v.classOriginal}
                        </div>
                    )}
                </main>
            </div>

            {/* ENGAGEMENT */}
            <div className="vw-engage vw-engage--panel">
                <PresenceBadge presenceKey="vehicles" entityId={entityId} />
                <LikeBar basePath={basePath} />
                <ReactionBar
                    basePath={basePath}
                    visibleReactions={[
                        "love",
                        "fire",
                        "funny",
                        "cool",
                        "helpful",
                        "applause",
                        "bug",
                    ]}
                    onSelect={(rid) => setSelectedReactionFromBar(rid)}
                />
                <Bookmark bookmarkKey={bookmarkKey} />
            </div>

            {/* DISCUSSION */}
            <div className="vw-discussion-panel">
                <Discussion basePath={basePath} />
            </div>
        </div>
    );
}
